import React, { useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Spinner } from '@/components/common';

export const ProductDialog = ({ trigger, onCreated, defaultStage = 'product_idea' }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [autoScore, setAutoScore] = useState(true);
  const [form, setForm] = useState({ name: '', description: '', source_price: '', sell_price: '', shipping_cost: '', supplier_url: '', product_url: '', image_url: '', research_notes: '' });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Product name is required'); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        source_price: form.source_price === '' ? null : parseFloat(form.source_price),
        sell_price: form.sell_price === '' ? null : parseFloat(form.sell_price),
        shipping_cost: form.shipping_cost === '' ? null : parseFloat(form.shipping_cost),
        stage: defaultStage,
        auto_score: autoScore,
      };
      const { data } = await api.post('/products', payload);
      toast.success(autoScore ? 'Product added — AI analysis running in background' : 'Product added');
      setOpen(false);
      setForm({ name: '', description: '', source_price: '', sell_price: '', shipping_cost: '', supplier_url: '', product_url: '', image_url: '', research_notes: '' });
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add product');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Add Product Idea</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Product name *</Label>
            <Input data-testid="product-form-name-input" value={form.name} onChange={set('name')} placeholder="e.g. Cordless Neck Massager" className="mt-1" />
          </div>
          <div>
            <Label>Description / who it's for</Label>
            <Textarea data-testid="product-form-description-input" value={form.description} onChange={set('description')} placeholder="What it does, who buys it, why now" className="mt-1" rows={3} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Source cost $</Label>
              <Input data-testid="product-form-source-price-input" type="number" step="0.01" value={form.source_price} onChange={set('source_price')} placeholder="8.50" className="mt-1" />
            </div>
            <div>
              <Label>Sell price $</Label>
              <Input data-testid="product-form-sell-price-input" type="number" step="0.01" value={form.sell_price} onChange={set('sell_price')} placeholder="39.95" className="mt-1" />
            </div>
            <div>
              <Label>Shipping $</Label>
              <Input data-testid="product-form-shipping-input" type="number" step="0.01" value={form.shipping_cost} onChange={set('shipping_cost')} placeholder="4.00" className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Supplier URL</Label>
            <Input data-testid="product-form-supplier-input" value={form.supplier_url} onChange={set('supplier_url')} placeholder="https://aliexpress.com/..." className="mt-1" />
          </div>
          <div>
            <Label>Image URL (optional)</Label>
            <Input data-testid="product-form-image-input" value={form.image_url} onChange={set('image_url')} placeholder="https://..." className="mt-1" />
          </div>
          <div>
            <Label>Research notes</Label>
            <Textarea data-testid="product-form-notes-input" value={form.research_notes} onChange={set('research_notes')} placeholder="Competitors seen, demand signals, angles you noticed..." className="mt-1" rows={2} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox data-testid="product-form-autoscore-checkbox" checked={autoScore} onCheckedChange={(v) => setAutoScore(!!v)} />
            Auto-run AI product analysis after adding
          </label>
          <Button data-testid="product-form-submit-button" onClick={submit} disabled={busy} className="w-full">
            {busy && <Spinner className="mr-2" />} Add Product
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
